import React, { useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { BASE_FUEL_REFILL_PATH } from '../../../../config/paths';
import {
  clearBaseFuelRefill,
  getBaseFuelRefill,
  putBaseFuelRefill,
} from '../../../../actions/traffic';
import {
  clearItinerary,
  getItineraryV2,
} from '../../../../actions';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import BaseFuelRefillForm from './BaseFuelRefillForm';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import { registeredBusGenerator } from '../../../../utils/app/json-generator-from-reducer';
import { TIMESTAMP_FORMAT, DATE_TIME_FORMAT } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';
import { getSubstring } from '../../../../utils/string';

const EditBaseFuelRefill = ({
  breadcrumbs,
  dispatchPutBaseFuelRefill,
  dispatchGetBaseFuelRefill,
  dispatchClearBaseFuelRefill,
  dispatchGetItinerary,
  dispatchClearItinerary,
  match: {
    params: { id: baseFuelRefillId },
  },
  loading,
  baseFuelRefill,
  itinerary,
}) => {
  useLayoutEffect(() => {
    dispatchGetBaseFuelRefill({ baseFuelRefillId });

    return () => {
      dispatchClearBaseFuelRefill();
      dispatchClearItinerary();
    };
  }, [baseFuelRefillId, dispatchGetBaseFuelRefill, dispatchClearBaseFuelRefill, dispatchClearItinerary]);

  useEffect(() => {
    if (
      !baseFuelRefill.isEmpty() &&
      baseFuelRefill.get('itineraryId') &&
      itinerary.isEmpty()
    ) {
      dispatchGetItinerary({ itineraryId: baseFuelRefill.get('itineraryId') });
    }
  }, [baseFuelRefill, itinerary, dispatchGetItinerary]);

  const onSubmit = (formValues) => {
    const newFormValues = { ...formValues };
    newFormValues.registeredBusId =
      formValues.registeredBusId && formValues.registeredBusId.value;
    newFormValues.createDate = tzNormalizeDate({
      date: formValues.createDate,
      format: TIMESTAMP_FORMAT,
    });
    newFormValues.itineraryId = getSubstring(formValues.itineraryId);
    
    dispatchPutBaseFuelRefill(baseFuelRefillId, newFormValues);
  };

  const generateInitialValues = (baseFuelRefillJson, itineraryJson) => {
    const initialValues = {
      registeredBusId: registeredBusGenerator(baseFuelRefillJson.registeredBus),
      fuelQuantity: baseFuelRefillJson.fuelQuantity,
      adblueQuantity: baseFuelRefillJson.adblueQuantity,
      comment: baseFuelRefillJson.comment,
      createDate: tzNormalizeDate({ date: baseFuelRefillJson.createDate }),
    };

    if (baseFuelRefillJson.itineraryId && !itinerary.isEmpty()) {
      initialValues.itineraryId = `${baseFuelRefillJson.itineraryId} - ${itineraryJson.route.name} ${tzNormalizeDate({
        date: itineraryJson.departureTime,
        format: DATE_TIME_FORMAT,
      })}`;
    } else {
      initialValues.itineraryId = getSubstring(baseFuelRefillJson.itineraryId);
    }

    return initialValues;
  };

  let content;

  if (loading || (baseFuelRefill.get('itineraryId') && itinerary.isEmpty())) {
    content = <Loader />;
  } else if (baseFuelRefill.isEmpty()) {
    content = <NoDataResource returnPage={BASE_FUEL_REFILL_PATH} />;
  } else {
    content = (
      <BaseFuelRefillForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(baseFuelRefill.toJS(), itinerary.toJS())}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Relleno de Combustible en Base"
      subtitle="Editar relleno de combustible en base"
      content={content}
    />
  );
};

EditBaseFuelRefill.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchPutBaseFuelRefill: PropTypes.func.isRequired,
  dispatchClearBaseFuelRefill: PropTypes.func.isRequired,
  dispatchGetBaseFuelRefill: PropTypes.func.isRequired,
  dispatchGetItinerary: PropTypes.func.isRequired,
  dispatchClearItinerary: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  baseFuelRefill: PropTypes.instanceOf(Immutable.Map).isRequired,
  itinerary: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditBaseFuelRefill.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchPutBaseFuelRefill: putBaseFuelRefill,
  dispatchGetBaseFuelRefill: getBaseFuelRefill,
  dispatchClearBaseFuelRefill: clearBaseFuelRefill,
  dispatchGetItinerary: getItineraryV2,
  dispatchClearItinerary: clearItinerary,
};

const mapStateToProps = (
  { TrafficUnit, ItineraryUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...TrafficUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Rellenos de Combustible en Base',
      href: BASE_FUEL_REFILL_PATH,
    },
    {
      text: 'Ver',
      href: `${BASE_FUEL_REFILL_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  baseFuelRefill: TrafficUnit.BaseFuelRefill.getIn(['current', 'content']),
  loading: !TrafficUnit.BaseFuelRefill.getIn(['current', 'activity']).isEmpty(),
  itinerary: ItineraryUnit.Itinerary.getIn(['current', 'content']),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditBaseFuelRefill);